import React, { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, Animated, Easing, TouchableOpacity } from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { Paprika, PaprikaTypography } from '../../styles/theme';
import { useTranslation } from 'react-i18next';

function formatTimeAgo(dateStr: string | null, t: (key: string, options?: Record<string, unknown>) => string): string | null {
  if (!dateStr) return null;
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('syncStatus.justNow');
  if (mins < 60) return t('syncStatus.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('syncStatus.hoursAgo', { count: hours });
  return t('syncStatus.daysAgo', { count: Math.floor(hours / 24) });
}

interface SyncStatusIndicatorProps {
  onRefresh?: () => void;
}

const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({ onRefresh }) => {
  const { t } = useTranslation();
  const isStale = useSelector((state: RootState) => state.groceryList?.isStale ?? false);
  const isRegenerating = useSelector((state: RootState) => state.groceryList?.isRegenerating ?? false);
  const generatedDate = useSelector((state: RootState) => state.groceryList?.generatedDate ?? null);
  const [, setTick] = useState(0);

  const spinAnim = useRef(new Animated.Value(0)).current;

  // Re-render every 60s to keep "Xm ago" fresh
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const timeAgo = formatTimeAgo(generatedDate, t);

  useEffect(() => {
    if (isRegenerating) {
      const animation = Animated.loop(
        Animated.timing(spinAnim, {
          toValue: 1,
          duration: 1000,
          easing: Easing.linear,
          useNativeDriver: true,
        })
      );
      animation.start();
      return () => animation.stop();
    } else {
      spinAnim.setValue(0);
    }
  }, [isRegenerating, spinAnim]);

  const spin = spinAnim.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  if (isRegenerating) {
    return (
      <View style={[styles.container, styles.regenerating]}>
        <Animated.View style={{ transform: [{ rotate: spin }] }}>
          <Feather name="refresh-cw" size={14} color={Paprika.accent} />
        </Animated.View>
        <Text style={[styles.text, styles.regeneratingText]}>{t('syncStatus.updating')}</Text>
      </View>
    );
  }

  if (isStale) {
    return (
      <TouchableOpacity
        style={[styles.container, styles.stale]}
        onPress={onRefresh}
        activeOpacity={0.7}
      >
        <Feather name="alert-circle" size={14} color="#B45309" />
        <Text style={[styles.text, styles.staleText]}>{t('common.tapToRefresh')}</Text>
      </TouchableOpacity>
    );
  }

  return (
    <View style={[styles.container, styles.upToDate]}>
      <Feather name="check-circle" size={14} color={Paprika.success} />
      <Text style={[styles.text, styles.upToDateText]}>
        {timeAgo ? t('syncStatus.updatedAgo', { ago: timeAgo }) : t('syncStatus.upToDate')}
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  text: {
    ...PaprikaTypography.caption,
    fontWeight: '500',
  },
  upToDate: {
    backgroundColor: `${Paprika.success}15`,
  },
  upToDateText: {
    color: Paprika.success,
  },
  stale: {
    backgroundColor: '#FEF3C7',
  },
  staleText: {
    color: '#B45309',
  },
  regenerating: {
    backgroundColor: `${Paprika.accent}15`,
  },
  regeneratingText: {
    color: Paprika.accent,
  },
});

export default SyncStatusIndicator;
